<template>
    <b-row>
        <b-col>
            <card title="Online Order Queue">
                <b-row>
                    <b-col>
                        <SearchComponent v-model="storeFilter" :label="`Store`" :option-label="searchLabel"
                            :prepopulate="true" :search-u-r-l="`/Store`" :params="{active: true}">
                        </SearchComponent>
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="programFilter" label="Program" :option-label="searchLabel" prepopulate
                            search-u-r-l="/Program" clearable>
                        </SearchComponent>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-table :fields="fields" :items="unlockedOnlineRefills" sort-icon-left>
                            <template #cell(prescriptions)="data">
                                <template v-for="(rx, idx) in data.item.prescriptions">
                                    <span
                                        :key="`${data.item.id}-${idx}`"
                                        :class="rx.lastShippedDate ? 'drug-hover' : ''"
                                        v-b-tooltip.hover.top
                                        :title="rx.lastShippedDate ? lastShippedTooltip(rx) : ''"
                                    >{{ rx.drugName }}</span><span v-if="idx < (data.item.prescriptions.length - 1)" :key="`sep-${data.item.id}-${idx}`">, </span>
                                </template>
                            </template>
                            <template #cell(programName)="data">
                                {{ getProgramName(data.item) }}
                            </template>
                            <template #cell(process)="data">
                                <b-button class="mx-1" variant="success" size="sm" @click="viewProcessingModal(data)">
                                    Process
                                </b-button>
                                <b-button class="mx-1" variant="danger" size="sm" @click="remove(data)"
                                    :disabled="!hasRemovePermission">
                                    Remove
                                </b-button>
                            </template>
                        </b-table>
                    </b-col>
                </b-row>
            </card>
        </b-col>

        <b-modal id="rx-modal" ref="rxModal" :title="batchModalTitle" hide-footer no-close-on-backdrop size="xl"
            @hide="onRxModalHide">
            <RxPage v-if="rxModalVisible" :prescriptionsInBatch="rxForBatch" :refillBatchMode="refillBatchMode"
                :card="false" @batchFinished="batchFinished">
            </RxPage>
            <hr>
            <b-button variant="dark" @click="batchCanceled">Cancel</b-button>
        </b-modal>
    </b-row>

</template>

<script lang="ts">
import { Component, Emit, Prop, Ref, Watch, Mixins } from 'vue-property-decorator';
import Axios from 'axios';
import moment from 'moment';
import SearchComponent from '@/components/SearchComponent.vue';
import PatientAllergyComponent from '@/components/GSDDAllergies/GSDDAllergyComponent.vue';
import { OnlineRefillOrder, OnlineRefillPrescription } from "@/models/OnlineRefillViewModel";
import { Store } from "@/models/Store";
import { Prescription } from "@/models/Prescription";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { BModal } from "bootstrap-vue";
import { Program } from "@/models/Program";
import { LockHandler } from '@/mixins/LockHandler';
import { EntityType } from '@/models/EntityType';
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';

@Component({
    name: "PatientWebOnlineOrderQueue",
    components: {
        SearchComponent,
        PatientAllergyComponent,
        RxPage: () => import('@/pages/Prescription/PrescriptionDetailsPage.vue')
    },
})

export default class PatientWebOnlineOrderQueue extends Mixins(LockHandler, AutoModeHotkeyMixin) {
    @Ref() readonly rxModal!: BModal;
    // Gates the embedded RxPage (PrescriptionDetailsPage): true only while the modal is open,
    // so closing destroys it and its LockHandler releases any prescription locks it still holds.
    private rxModalVisible = false;
    @Prop({ default: true, type: Boolean }) private refillBatchMode!: boolean;
    @Prop({ default: "New Refills In Batch" }) private batchModalTitle!: string;
    private unprocessedOnlineRefills: OnlineRefillOrder[] = [];
    private selectedOnlineRefill: any = {} as any;
    private storeFilter: Store = {} as Store;
    private programFilter: Program | null = null;
    private prescriptionsSelected: Prescription[] = [];
    private prescriptions: Array<Prescription> = [];
    private fields = [
        { key: 'patientName', label: 'Patient Name', sortable: true },
        { key: 'created', formatter: this.convertDate, label: 'Date', sortable: true },
        { key: 'prescriptions', label: 'Drug Name', formatter: this.drugNameFormatter, sortable: true },
        { key: 'programName', label: 'Program Name', sortable: true },
        { key: 'patientPickupPreference', label: 'Patient Requested', formatter: this.formatPickupPreference, sortable: true },
        { key: 'amount', label: 'Copay', formatter: this.convertAmount },
        { key: 'process', class: 'process-col', label: "" },
    ];

    formatPickupPreference(value: boolean | null | undefined): string {
        if (value === true) return 'Pickup';
        if (value === false) return 'Delivery';
        return '—';
    }

    private rxForBatch: Prescription[] = [];
    private processingOrderIds: Set<number> = new Set();

    drugNameFormatter(items: OnlineRefillPrescription[]) {
        return items?.map(i => i.drugName).join(', ') || '';
    }

    lastShippedTooltip(rx: OnlineRefillPrescription): string {
        if (!rx?.lastShippedDate) return 'Last shipped: never';
        const date = moment.utc(rx.lastShippedDate).format('L');
        return rx.lastShippedIsEquivalent
            ? `Last shipped: ${date} (EQV)`
            : `Last shipped: ${date}`;
    }

    get prescriptionsToShow(): Array<Prescription> {
        return this.prescriptions.map(rx => Object.assign(new Prescription(), rx));
    }

    get unlockedOnlineRefills(): OnlineRefillOrder[] {
        return this.unprocessedOnlineRefills.filter(o => !o.lockedBy);
    }

    get hasRemovePermission(): boolean {
        console.log("this.$permissions: ", this.$permissions);
        return this.$permissions.some(p => p.permissionCodeName == "Processing/Online Order Queue Remove");
    }

    async remove(selectedOrder: any) {
        try {
            const resp = await this.$bvModal.msgBoxConfirm("Are you sure you want to remove this order?",
                { title: "Remove Order", okTitle: "Remove", okVariant: "danger", noFade: true, centered: true });
            if (!resp) return;

            if (selectedOrder.item.legacyPortal) {
                await Axios.post(`/api/PatientWeb/Process/${selectedOrder.item.id}`);
            } else {
                await Axios.post(`/api/PatientWeb/Process/PatientApp/${selectedOrder.item.id}`);
            }
            this.$notification(NotificationOptions.success("Order Removed"));
            this.getUnprocessedOnlineRefills();

            await this.handleAutoModeCompletion();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    viewProcessingModal(selectedOrder: any) {
        const lockUrl = selectedOrder.item.legacyPortal
            ? `PatientWeb/${selectedOrder.item.id}/lock`
            : `PatientWeb/app/${selectedOrder.item.id}/lock`;

        // Under Auto Mode this page advances order-to-order without ever being destroyed, so
        // setExclusiveLock must reap the previous order's lock or locks would accumulate.
        this.setExclusiveLock(lockUrl, 60000)
            .then(result => {
                // Null means the call was superseded or the acquire never landed — no lock
                // is held, so opening the processing modal would allow concurrent edits.
                if (!result) return;
                this.selectedOnlineRefill = selectedOrder;

                let scripts = this.selectedOnlineRefill.item.prescriptions.map((rx: any) => {
                    let script = new Prescription();
                    script.storeID = rx.storeId;
                    script.rxNumber = rx.rxNumber;
                    script.rfNumber = rx.rfNumber;
                    return script;
                });
                this.openBatch(scripts);
            })
            .catch(error => {
                console.log(`added lock -- error == ${error}`);
                if (error.response && error.response.status == 418) {
                    this.getUnprocessedOnlineRefills();
                    this.showLockedMessage(
                        EntityType.OnlineOrder,
                        selectedOrder.item.id?.toString(),
                        selectedOrder.item.id?.toString(),
                        error.response.data
                    );
                }
            });
    }

    openBatch(rxForBatch: Array<Prescription>) {
        console.log("rxForBatch: ", rxForBatch);
        if (this.refillBatchMode) {
            this.prescriptionsSelected = rxForBatch;
            this.prescriptions = rxForBatch;
            if (!this.prescriptionsSelected?.length) {
                // The order lock was already acquired in viewProcessingModal but no modal will
                // open to release it — reap it instead of leaving it renewing.
                this.clearAllLocks();
                return;
            }

            const sameStoreRx = (rx: any, rx2: any) => {
                return rx.storeID == rx2.storeID && rx.rxNumber == rx2.rxNumber;
            };

            const findMaxRfInStoreRxGroup = (rx1: Prescription, rx2: Prescription): Prescription => {
                return (rx1.rfNumber || 0) > (rx2.rfNumber ?? 0) ? rx1 : rx2;
            };

            //Find the distinct StoreId - RxNumber combinations in the selection
            const distinctStoreRxList = this.prescriptionsSelected.filter((rx: any, i: any, rxs: any) => {
                const i2 = rxs.findIndex((rx2: any) => sameStoreRx(rx, rx2));
                return i2 == i;
            }).map(rx => {
                return { storeID: rx.storeID, rxNumber: rx.rxNumber };
            });

            //Find the prescription with the max RfNumber for each distinct StoreId - RxNumber combination
            this.rxForBatch = distinctStoreRxList.map(ids => {
                const group = this.prescriptionsToShow.filter((rx: any) => sameStoreRx(rx, ids));
                return Object.assign(new Prescription(), group.reduce(findMaxRfInStoreRxGroup));
            });
        } else {
            this.rxForBatch = rxForBatch;
        }

        this.rxModalVisible = true;
        this.rxModal.show();
    }

    // Wired to @hide (not @hidden): 'hide' fires synchronously when the modal starts closing,
    // before Auto Mode advances to the next order — so the reap (and its lock-generation bump)
    // can't race the next order's lock acquisition, and the embedded RxPage is destroyed
    // (releasing its prescription locks) before a fresh one mounts for the next order.
    // Covers X/ESC, Cancel, and batch completion.
    onRxModalHide() {
        this.rxModalVisible = false;
        this.clearAllLocks();
    }

    @Emit()
    batchCanceled() {
        // hide() emits 'hide' synchronously, so onRxModalHide owns the lock reap.
        this.rxModal.hide();
    }

    @Emit("batchFinished")
    async batchFinished(...args: any[]) {
        console.log("batch Finished args:", args);

        const onlineOrderId: number | undefined = this.selectedOnlineRefill?.item?.id;
        if (onlineOrderId == null || this.processingOrderIds.has(onlineOrderId)) {
            // batchFinished can fire more than once per Save; suppress re-entry
            return;
        }
        this.processingOrderIds.add(onlineOrderId);

        let url = `order/scripts/${onlineOrderId}`;
        if (!this.selectedOnlineRefill.item.legacyPortal) {
            url = `order/scripts/app/${onlineOrderId}`;
        }
        try {
            const orderResponse = await Axios.post(url, this.rxForBatch);
            console.log("orderResponse: ", orderResponse);

            const warning: string | undefined = orderResponse.data?.warning;
            if (warning) {
                this.$notification(NotificationOptions.warning(warning));
            }

            this.rxModal.hide();

            await this.getUnprocessedOnlineRefills();

            await this.handleAutoModeCompletion();
        } catch (orderError) {
            console.warn(orderError);
            this.rxModal.hide();
            if (orderError.response?.status === 409) {
                this.$notification(NotificationOptions.warning(orderError.response.data));
                await this.getUnprocessedOnlineRefills();
            } else {
                this.$notification(NotificationOptions.errorSaveNotificationPreset("Order", orderError));
            }
        } finally {
            this.processingOrderIds.delete(onlineOrderId);
        }
    }

    async created() {
        await Axios.get(`/Store/`).then(response => {
            this.storeFilter = response.data[0];
        });

        await this.getUnprocessedOnlineRefills();

        const orderId = this.$route.params.orderId;
        if (orderId) {
            await this.loadSingleItem(parseInt(orderId));
        }
    }

    @Watch('$route')
    async onRouteChange(newRoute: any, oldRoute: any) {
        if (newRoute.name !== oldRoute.name) {
            return;
        }

        const oldOrderId = oldRoute.params.orderId;
        const newOrderId = newRoute.params.orderId;

        if (oldOrderId !== newOrderId && newOrderId) {
            console.log('Route orderId parameter changed, loading new online order item');
            await this.getUnprocessedOnlineRefills();
            await this.loadSingleItem(parseInt(newOrderId));
        }
    }

    async loadSingleItem(id: number) {
        try {
            let order = this.unprocessedOnlineRefills.find(o => o.id === id);

            if (!order) {
                try {
                    const response = await Axios.get<OnlineRefillOrder>(`/PatientWeb/${id}?legacyPortal=false`);
                    order = response.data;
                } catch (patientAppError) {
                    try {
                        const response = await Axios.get<OnlineRefillOrder>(`/PatientWeb/${id}?legacyPortal=true`);
                        order = response.data;
                    } catch (legacyPortalError) {
                        throw new Error(`Order ${id} not found in either Patient App or Legacy Portal`);
                    }
                }
            }

            if (order) {
                this.viewProcessingModal({ item: order });
            }
        } catch (error) {
            console.error('Error loading single online order:', error);
            this.$notification(NotificationOptions.error('Failed to load online order'));
        }
    }

    getProgramName(item: OnlineRefillOrder) {
        return item.programName;
    }

    @Watch('storeFilter')
    @Watch('programFilter')
    getUnprocessedOnlineRefills() {
        Axios.get(`/PatientWeb/GetUnprocessed`).then(response => {
            this.unprocessedOnlineRefills = response.data;
            if (this.storeFilter) {
                this.unprocessedOnlineRefills = this.unprocessedOnlineRefills.filter((order => order.storeId == this.storeFilter.id));
            }
            if (this.programFilter) {
                console.log("programFilter =", this.programFilter);
                console.log("unprocessedOnlineRefills = ", this.unprocessedOnlineRefills);
                this.unprocessedOnlineRefills = this.unprocessedOnlineRefills.filter((order => this.getProgramName(order) == this.programFilter!.name));
            }
        }).catch(err => this.$notification(NotificationOptions.error(err)));

    }

    convertDate(date: string) {
        return moment.utc(date).format("L");
    }

    convertAmount(amt: number) {
        return '$' + amt.toFixed(2);
    }

    async handleAutoModeCompletion() {
        await this.handleAutoModeNextItem();
    }

    searchLabel(object: Store) {
        if (object?.id)
            return object.name;
        else return '';
    }
}
</script>

<style scoped>

::v-deep(.process-col) {
    width: 210px;
}

.drug-hover {
    text-decoration: underline dotted;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;
    cursor: help;
}

</style>
